import { useState } from 'react';
import { usePreviaConversao, useConfirmarConversao } from '@/api/tabelas-preco-tributos';
import { formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { hojeIso, mensagemErroTabela } from './tabela-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const BOTAO = 'h-[34px] rounded-[10px] border border-input bg-card px-3 text-small font-semibold text-ink-body hover:bg-line-soft disabled:opacity-50';
const BOTAO_PRIMARIO = 'h-[34px] rounded-[10px] bg-primary px-4 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50';

/** Quantos itens entram no efeito na tela (a API ja devolve a amostra de 5). */
const ITENS_NA_TELA = 5;

export interface PreviaConversaoProps {
  tabelaId: string;
  podeEditar: boolean;
  onConcluida: (texto: string) => void;
}

/**
 * "Nova vigência estruturada a partir desta": mostra a prévia da API (bloqueios, avisos, pendentes, efeito antes × depois)
 * e so grava ao confirmar. Cancelar nao grava nada.
 */
export function PreviaConversao({ tabelaId, podeEditar, onConcluida }: PreviaConversaoProps) {
  const [aberto, setAberto] = useState(false);
  const [validFrom, setValidFrom] = useState(hojeIso());
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const previa = usePreviaConversao(tabelaId, aberto && podeEditar);
  const confirmar = useConfirmarConversao();

  if (!podeEditar) return null;

  const dados = previa.data;
  const bloqueada = !dados || !dados.corpo || dados.bloqueios.length > 0;

  async function confirmarConversao(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!dados?.corpo) return setErro('A prévia não permite a conversão. Veja os bloqueios.');
    if (validFrom.trim() === '') return setErro('Informe o início da nova vigência');
    try {
      await confirmar.mutateAsync({ tabelaId, validFrom, ...(motivo.trim() ? { motivo: motivo.trim() } : {}), corpo: dados.corpo });
      setAberto(false);
      setMotivo('');
      onConcluida('Nova vigência estruturada registrada. A anterior foi encerrada na data de início.');
    } catch (err) {
      setErro(mensagemErroTabela(err));
    }
  }

  if (!aberto) {
    return (
      <button type="button" className={BOTAO} onClick={() => setAberto(true)}>
        Nova vigência estruturada a partir desta
      </button>
    );
  }

  return (
    <section aria-label="Prévia da conversão" className="flex flex-col gap-4 rounded-xl border border-border p-4">
      <h4 className="text-small font-semibold">Prévia da conversão para o modo estruturado</h4>

      {previa.isError && !dados && <p role="alert" className="text-small text-danger-ink">{mensagemErroTabela(previa.error)}</p>}
      {!dados && !previa.isError && <p role="status" className="text-small text-ink-muted">Calculando a prévia…</p>}

      {dados && (
        <>
          {dados.bloqueios.length > 0 && (
            <ul aria-label="Bloqueios" className="flex flex-col gap-1 text-small text-danger-ink">
              {dados.bloqueios.map((b) => (
                <li key={b.codigo}>{b.mensagem}</li>
              ))}
            </ul>
          )}
          {dados.avisos.length > 0 && (
            <ul aria-label="Avisos da conversão" className="flex flex-col gap-1 text-small text-ink-body">
              {dados.avisos.map((a) => (
                <li key={a.codigo}>{a.mensagem}</li>
              ))}
            </ul>
          )}
          {dados.pendentes.length > 0 && (
            <div>
              <h5 className="mb-1 text-caption font-semibold text-ink-secondary">Pendentes: a conversão não classificou estas linhas (você decide depois na aba Tributos)</h5>
              <ul aria-label="Linhas pendentes" className="flex flex-col gap-1 text-small">
                {dados.pendentes.map((x, i) => (
                  <li key={`${x.descricao}-${i}`}>{`${x.descricao} · ${formatarFracaoPercentual(x.taxa)} · ${x.motivo}`}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-caption text-ink-muted">Despesas variáveis que continuam: {dados.despesasMantidas.length}</p>

          {dados.efeito.length > 0 && (
            <table className="w-full text-small" aria-label="Efeito no preço">
              <thead>
                <tr className="text-left text-caption text-ink-muted">
                  <th scope="col" className="py-1 font-medium">Item</th>
                  <th scope="col" className="py-1 text-right font-medium">Antes</th>
                  <th scope="col" className="py-1 text-right font-medium">Depois</th>
                  <th scope="col" className="py-1 text-right font-medium">Variação</th>
                </tr>
              </thead>
              <tbody>
                {dados.efeito.slice(0, ITENS_NA_TELA).map((x) => (
                  <tr key={x.itemId} className="border-t border-line-soft">
                    <td className="py-1.5">{x.codigo}</td>
                    <td className="tabular py-1.5 text-right">{x.erro ? '—' : formatarMoeda(x.antes)}</td>
                    <td className="tabular py-1.5 text-right">{x.erro ? x.erro : formatarMoeda(x.depois)}</td>
                    <td className="tabular py-1.5 text-right">{x.erro ? '—' : formatarFracaoPercentual(x.variacao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <form onSubmit={confirmarConversao} aria-label="Confirmar conversão" className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="conv-inicio" className={ROTULO}>Início</label>
          <input id="conv-inicio" type="date" className={CAMPO} min={hojeIso()} value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </div>
        <div className="min-w-[240px] flex-1">
          <label htmlFor="conv-motivo" className={ROTULO}>Motivo</label>
          <input id="conv-motivo" className={CAMPO} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        <button type="button" className={BOTAO} onClick={() => setAberto(false)}>Cancelar</button>
        <button type="submit" className={BOTAO_PRIMARIO} disabled={bloqueada || confirmar.isPending}>Confirmar conversão</button>
      </form>
      {erro && <p role="alert" className="text-small text-danger-ink">{erro}</p>}
    </section>
  );
}
